import { useCallback } from 'react'

import {
    getFeatureFlagsForEvent,
    getDocumentReferrer,
    getCurrentWindowUrl,
    getCardDisplayedProductsForEvent,
    toSnakeCase,
    getCardApr,
} from '@mediaingenuity/totallymoney.cardloanutils'
import {
    Category,
    trackEvent,
} from '@mediaingenuity/totallymoney.trackingevents'

import {
    CreditCardAlternativeProduct,
    ScoredCreditCardProduct,
    ScoredCreditCardProductOfferDetail,
} from '@appTypes/EligibilityApi.interface'

import { logMissingProductClickFields } from '@utils/logMissingProductClickFields'

import useAppQueryState from './useAppQueryState'
import { useBasin } from './useBasin'
import useFeatureFlags from './useFeatureFlags'

type logResultsScreenDisplayedEventProps =
    | {
          category: 'credit_cards'
          displayedProducts?: ScoredCreditCardProduct[]
          highlightProduct: ScoredCreditCardProduct | null
          maxCreditLimit?: ScoredCreditCardProduct['maxCreditLimit']
          numberOfCardsToShow?: number
      }
    | {
          category: 'credit_cards_alt'
          displayedProducts?: CreditCardAlternativeProduct[]
          highlightProduct: null
          maxCreditLimit?: ScoredCreditCardProduct['maxCreditLimit']
          numberOfCardsToShow?: number
      }

type LogCreditCardsProductClickBase = {
    category: Category
    clickId: string
    productRankOrder: number | null
    productShownOrder: number | null
}

type LogProductClickEventProps = LogCreditCardsProductClickBase & {
    creditCard: Pick<
        ScoredCreditCardProduct,
        | 'name'
        | 'productCode'
        | 'guaranteedRate'
        | 'maxCreditLimit'
        | 'provider'
        | 'offerDetail'
    >
}

type LogAlternativeProductClickEventProps = {
    altCreditCard: Pick<
        CreditCardAlternativeProduct,
        'name' | 'productCode' | 'apr'
    >
} & LogCreditCardsProductClickBase

// This hook handles logging events 2.0 events to Basin
const useLogEvents2 = () => {
    const { appQueryState } = useAppQueryState()
    const { send } = useBasin()
    const { featureFlags } = useFeatureFlags()

    const logProductClickEvent = useCallback(
        ({
            creditCard,
            category,
            clickId,
            productShownOrder,
            productRankOrder,
        }: LogProductClickEventProps) => {
            logMissingProductClickFields({
                category,
                clickId,
                customerId: appQueryState.customerId,
                productCode: creditCard?.productCode,
                scoreId: appQueryState.scoreId,
            })

            const payload = trackEvent('product_click', {
                apr: getCardApr({
                    guaranteedRate: creditCard.guaranteedRate,
                    offerDetail: creditCard.offerDetail,
                }),
                lender: creditCard.provider,
                product_code: creditCard.productCode,
                product_name: creditCard.name,
                product_shown_order: productShownOrder,
                product_rank_order: productRankOrder,
                max_credit_limit: creditCard.maxCreditLimit,
                guaranteed_credit_limit:
                    creditCard.guaranteedRate?.guaranteedCreditLimit,

                category,
                click_id: clickId,
                client: 'web',
                customer_id: appQueryState.customerId,
                downsell: appQueryState.referrer === 'loansdownsell',
                gclid: appQueryState.googleClickId,
                origin: getDocumentReferrer(),
                score_id: appQueryState.scoreId!,
                send_id: appQueryState.sendId,
                url: getCurrentWindowUrl(),
                utm_campaign: appQueryState.campaign,
                utm_content: appQueryState.utmContent,
                utm_medium: appQueryState.utmMedium,
                utm_source: appQueryState.mediaSource,
                variations: getFeatureFlagsForEvent(featureFlags),
            })

            send('product_click', payload)
        },
        [send, appQueryState]
    )
    const logAlternativeProductClickEvent = useCallback(
        ({
            altCreditCard,
            category,
            clickId,
            productShownOrder,
            productRankOrder,
        }: LogAlternativeProductClickEventProps) => {
            logMissingProductClickFields({
                category,
                clickId,
                customerId: appQueryState.customerId,
                productCode: altCreditCard?.productCode,
                scoreId: appQueryState.scoreId,
            })

            const payload = trackEvent('product_click', {
                apr: altCreditCard.apr,
                lender: altCreditCard.name,
                product_code: altCreditCard.productCode,
                product_name: altCreditCard.name,
                product_shown_order: productShownOrder,
                product_rank_order: productRankOrder,

                category,
                click_id: clickId,
                client: 'web',
                customer_id: appQueryState.customerId,
                downsell: appQueryState.referrer === 'loansdownsell',
                gclid: appQueryState.googleClickId,
                origin: getDocumentReferrer(),
                score_id: appQueryState.scoreId!,
                send_id: appQueryState.sendId,
                url: getCurrentWindowUrl(),
                utm_campaign: appQueryState.campaign,
                utm_content: appQueryState.utmContent,
                utm_medium: appQueryState.utmMedium,
                utm_source: appQueryState.mediaSource,
                variations: getFeatureFlagsForEvent(featureFlags),
            })

            send('product_click', payload)
        },
        [send, appQueryState]
    )

    const logResultsScreenDisplayedEvent = useCallback(
        ({
            category,
            displayedProducts,
            maxCreditLimit,
            numberOfCardsToShow,
            highlightProduct,
        }: logResultsScreenDisplayedEventProps) => {
            const payload = trackEvent('results_screen_displayed', {
                url: getCurrentWindowUrl(),
                origin: getDocumentReferrer(),
                variations: getFeatureFlagsForEvent(featureFlags),
                requested_credit_limit: maxCreditLimit,
                send_id: appQueryState.sendId,
                category,
                client: 'web',
                filter: appQueryState.category
                    ? toSnakeCase(appQueryState.category!)
                    : undefined,
                order_by: appQueryState.sortBy
                    ? toSnakeCase(appQueryState.sortBy!)
                    : undefined,
                displayed_products: getCardDisplayedProductsForEvent<
                    ScoredCreditCardProduct | CreditCardAlternativeProduct
                >({
                    products: displayedProducts!,
                    numberOfCardsToShow: numberOfCardsToShow,
                    highlightProduct: highlightProduct,
                    category,
                }),
                customer_id: appQueryState.customerId,
                gclid: appQueryState.googleClickId,
                utm_campaign: appQueryState.campaign,
                utm_content: appQueryState.utmContent,
                utm_medium: appQueryState.utmMedium,
                utm_source: appQueryState.mediaSource,
                score_id: appQueryState.scoreId!,
            })

            send('results_screen_displayed', payload)
        },
        [send, appQueryState]
    )

    return {
        logProductClickEvent,
        logAlternativeProductClickEvent,
        logResultsScreenDisplayedEvent,
    }
}

export default useLogEvents2
